import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, RefreshCw, MoreVertical, Info } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import StatusPill from "@/components/StatusPill";

const dayISO = (d) => {
  const c = new Date(d);
  c.setHours(0, 0, 0, 0);
  return c.toISOString();
};
const fmtDate = (d) =>
  new Date(d).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
const fmtRange = (start, end) => {
  const a = new Date(start);
  const b = new Date(end);
  const bEff = new Date(b.getTime() - 1);
  return `${a.toLocaleDateString(undefined, { day: "numeric", month: "short" })} – ${bEff.toLocaleDateString(undefined, { day: "numeric", month: "short" })}`;
};
const fmtTime = (iso) =>
  new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
const dayKey = (iso) => new Date(iso).toDateString();

function AssignMenu({ job, techs, onAssign, onOpen }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();
  useEffect(() => {
    const onClickOutside = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);
  return (
    <div ref={ref} className="relative">
      <button
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        aria-label="Assign menu"
        data-testid={`sched-menu-${job.id}`}
        className="w-8 h-8 rounded-md flex items-center justify-center text-[#6A6E85] hover:bg-[#ECEDF6] transition-colors duration-200"
      >
        <MoreVertical size={16} />
      </button>
      {open && (
        <div className="absolute right-0 top-9 z-30 bg-white border border-[#E4E6EF] rounded-lg shadow-lg py-1 min-w-[180px]">
          <div className="px-3 py-1.5 text-[10px] uppercase tracking-widest text-[#6A6E85] font-semibold border-b border-[#E4E6EF]">
            Assign to
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); onAssign(null); setOpen(false); }}
            className="block w-full text-left px-3 py-2 text-sm hover:bg-[#F5F6FA] text-[#14162E]"
            data-testid={`sched-assign-${job.id}-unassigned`}
          >
            Unassigned
          </button>
          {techs.map((t) => (
            <button
              key={t.id}
              onClick={(e) => { e.stopPropagation(); onAssign(t.id); setOpen(false); }}
              className="block w-full text-left px-3 py-2 text-sm hover:bg-[#F5F6FA] text-[#14162E]"
              data-testid={`sched-assign-${job.id}-${t.id}`}
            >
              {t.name}
            </button>
          ))}
          <div className="border-t border-[#E4E6EF] mt-1 pt-1">
            <button
              onClick={(e) => { e.stopPropagation(); onOpen(); setOpen(false); }}
              className="block w-full text-left px-3 py-2 text-sm hover:bg-[#F5F6FA] text-[#181854] font-medium"
            >
              Open job →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Schedule() {
  const navigate = useNavigate();
  const [range, setRange] = useState("day");
  const [anchor, setAnchor] = useState(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [board, setBoard] = useState(null);
  const [techs, setTechs] = useState([]);
  const [dragId, setDragId] = useState(null);

  const load = useCallback(() => {
    const params = { date: dayISO(anchor), range_days: range === "day" ? 1 : 7 };
    api.get("/schedule", { params }).then((r) => setBoard(r.data));
  }, [anchor, range]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/techs").then((r) => setTechs(r.data)); }, []);

  const shift = (days) => {
    const d = new Date(anchor);
    d.setDate(d.getDate() + days);
    setAnchor(d);
  };

  const doAssign = async (jobId, techId) => {
    try {
      await api.patch(`/jobs/${jobId}`, { assigned_tech_id: techId });
      toast.success(techId ? "Job assigned" : "Job unassigned");
      load();
    } catch { toast.error("Failed to reassign"); }
  };

  const onDrop = (techId) => {
    if (!dragId) return;
    doAssign(dragId, techId);
    setDragId(null);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-[#181854] tracking-tight">Schedule board</h1>
          <p className="text-[#6A6E85] mt-1">Drag a job onto a tech, or use the menu button on any card.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="bg-white border border-[#E4E6EF] rounded-lg overflow-hidden flex">
            <button
              onClick={() => setRange("day")}
              data-testid="sched-toggle-day"
              className={`px-4 py-2 text-sm font-medium transition-colors duration-200 ${
                range === "day" ? "bg-[#181854] text-white" : "text-[#181854] hover:bg-[#ECEDF6]"
              }`}
            >
              Day
            </button>
            <button
              onClick={() => setRange("week")}
              data-testid="sched-toggle-week"
              className={`px-4 py-2 text-sm font-medium transition-colors duration-200 ${
                range === "week" ? "bg-[#181854] text-white" : "text-[#181854] hover:bg-[#ECEDF6]"
              }`}
            >
              Week
            </button>
          </div>
          <div className="bg-white border border-[#E4E6EF] rounded-lg flex items-center">
            <button
              onClick={() => shift(range === "day" ? -1 : -7)}
              className="px-2 py-2 hover:bg-[#ECEDF6] transition-colors duration-200"
              data-testid="sched-prev"
              aria-label="Previous"
            >
              <ChevronLeft size={16} />
            </button>
            <span className="px-3 text-sm font-medium text-[#181854]" data-testid="sched-range-label">
              {range === "day"
                ? fmtDate(anchor)
                : fmtRange(anchor, new Date(anchor.getTime() + 7 * 86400000))}
            </span>
            <button
              onClick={() => shift(range === "day" ? 1 : 7)}
              className="px-2 py-2 hover:bg-[#ECEDF6] transition-colors duration-200"
              data-testid="sched-next"
              aria-label="Next"
            >
              <ChevronRight size={16} />
            </button>
          </div>
          <button
            onClick={load}
            className="w-9 h-9 bg-white border border-[#E4E6EF] rounded-lg flex items-center justify-center hover:bg-[#ECEDF6] transition-colors duration-200"
            aria-label="Refresh"
            data-testid="sched-refresh"
          >
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      {!board ? (
        <div className="text-[#6A6E85]">Loading…</div>
      ) : (
        <div className="gt-scroll flex gap-4 overflow-x-auto pb-4">
          {board.columns.map((col) => {
            const techId = col.tech?.id || null;
            const jobsByDay = {};
            (col.jobs || []).forEach((j) => {
              const k = dayKey(j.scheduled_at);
              (jobsByDay[k] ||= []).push(j);
            });
            const dayKeys = Object.keys(jobsByDay).sort(
              (a, b) => new Date(a) - new Date(b)
            );
            return (
              <div
                key={techId || "unassigned"}
                data-testid={`sched-col-${techId || "unassigned"}`}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => onDrop(techId)}
                className={`min-w-[290px] w-72 flex-shrink-0 bg-[#ECEDF6] rounded-xl p-3 ${
                  dragId ? "ring-2 ring-[#4F9CE0]/40" : ""
                }`}
              >
                <div className="flex items-center justify-between px-2 py-1.5 mb-2">
                  <div>
                    <div className="text-sm font-bold text-[#181854]">{col.label}</div>
                    <div className="text-xs text-[#6A6E85]">
                      {col.jobs.length} job{col.jobs.length === 1 ? "" : "s"}
                    </div>
                  </div>
                  {!col.tech && (
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-[#4F9CE0]">
                      drop here
                    </span>
                  )}
                </div>
                {col.jobs.length === 0 && (
                  <div className="text-center py-8 px-2">
                    <p className="text-xs text-[#6A6E85]">No jobs on this {range}.</p>
                    <button
                      onClick={() => shift(range === "day" ? 1 : 7)}
                      className="mt-2 text-xs font-semibold text-[#4F9CE0] hover:underline"
                    >
                      Try next {range} →
                    </button>
                  </div>
                )}
                <div className="space-y-2">
                  {range === "week"
                    ? dayKeys.map((dk) => (
                        <div key={dk}>
                          <div className="text-[10px] uppercase tracking-widest font-semibold text-[#6A6E85] px-1 mt-1 mb-1">
                            {fmtDate(dk)} · {jobsByDay[dk].length}
                          </div>
                          {jobsByDay[dk].map((j) => (
                            <ScheduleCard
                              key={j.id}
                              job={j}
                              techs={techs}
                              onDragStart={() => setDragId(j.id)}
                              onDragEnd={() => setDragId(null)}
                              onAssign={(t) => doAssign(j.id, t)}
                              onOpen={() => navigate(`/office/jobs/${j.id}`)}
                              showDate={false}
                            />
                          ))}
                        </div>
                      ))
                    : (col.jobs || []).map((j) => (
                        <ScheduleCard
                          key={j.id}
                          job={j}
                          techs={techs}
                          onDragStart={() => setDragId(j.id)}
                          onDragEnd={() => setDragId(null)}
                          onAssign={(t) => doAssign(j.id, t)}
                          onOpen={() => navigate(`/office/jobs/${j.id}`)}
                          showDate={false}
                        />
                      ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <p className="text-xs text-[#6A6E85] flex items-center gap-1.5">
        <Info size={12} /> Tip: click a card to open it, drag it to a column to assign, or use the ⋮ menu.
      </p>
    </div>
  );
}

function ScheduleCard({ job, techs, onDragStart, onDragEnd, onAssign, onOpen, showDate }) {
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      data-testid={`sched-card-${job.id}`}
      className={`status-border-${job.status} bg-white rounded-lg p-3 shadow-sm hover:shadow-md transition-shadow duration-200`}
    >
      <div className="flex items-start justify-between gap-2">
        <button
          onClick={onOpen}
          className="text-left flex-1 min-w-0"
        >
          <p className="text-sm font-semibold text-[#14162E] leading-tight">{job.title}</p>
          <p className="text-xs text-[#6A6E85] mt-1 truncate">{job.client_name}</p>
          <p className="text-xs text-[#6A6E85] truncate">{job.site_address}</p>
          <p className="text-[11px] text-[#181854] font-medium mt-2">
            {showDate ? `${fmtDate(job.scheduled_at)} · ` : ""}{fmtTime(job.scheduled_at)}
          </p>
        </button>
        <div className="flex flex-col items-end gap-1.5">
          <StatusPill status={job.status} />
          <AssignMenu job={job} techs={techs} onAssign={onAssign} onOpen={onOpen} />
        </div>
      </div>
    </div>
  );
}
